import { observer } from "mobx-react";
import { IOrder } from "../../stores/order";
import { Modal } from "react-bootstrap";
import {
  DeliveryMethod,
  IModal,
  OfferTypeEnum,
  OrderStatus,
  PaymentMethod,
  PaymentStatus,
} from "../../stores/index.d";
import {
  currencyFormatter,
  formatDate,
  getDisplayDeliveryType,
  getDisplayPaymentStatusForOrder,
  getDisplayVehicleType,
  getOrderDisplayStatusForOrder,
  zeroPad,
} from "../../utils/functions";
import { useReactToPrint } from "react-to-print";
import { useEffect, useRef } from "react";
import { useCookies } from "react-cookie";
import { useAuth } from "../../contexts/auth-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCheckCircle,
  faPhone,
  faPrint,
} from "@fortawesome/free-solid-svg-icons";
import { useStores } from "../../stores/root-store";
import OrderReceipt from "./order-receipt";
import { getReceiptPageStyle } from "./receipt-print";

interface IOderDetails extends IModal {
  order: IOrder;
}

const OrderDetails = observer(({ order, show, setShowModal }: IOderDetails) => {
  const [cookies] = useCookies();
  const { isShopAdmin, isShopUser } = useAuth();
  const { userStore } = useStores();
  const { getUserDetails, userDetails } = userStore;

  const st = getOrderDisplayStatusForOrder(order);
  const pay = getDisplayPaymentStatusForOrder(order);
  const del = getDisplayDeliveryType(order.orderDeliveryType);
  const receiptRef = useRef<HTMLDivElement>(null);

  const deliveryCost =
    order.orderDeliveryType === DeliveryMethod.Delivery
      ? order.deliveryCost ?? 0
      : 0;

  const cartSubTotal =
    (order.orderTotal ?? 0) +
    (order.voucherDiscount ?? 0) -
    (order.taxAmount ?? 0) -
    deliveryCost;

  const vehType =
    order.deliveryDriver &&
    getDisplayVehicleType(order.deliveryDriver?.vehicleType);

  const handlePrint = useReactToPrint({
    content: () => receiptRef.current,
    pageStyle: () => getReceiptPageStyle(receiptRef.current),
  });

  useEffect(() => {
    order?.orderedUserId && getUserDetails(order?.orderedUserId);
  }, [order?.orderedUserId]);

  return (
    <Modal
      show={show}
      onHide={() => setShowModal(false)}
      centered
      scrollable
      dialogClassName="modal-lg"
      backdrop="static"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <h4>Order # {zeroPad(order.orderId, 8)}</h4>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="cart-table table-responsive mb-4 ps-2 pe-2">
          {order.paymentMethod == PaymentMethod.Card &&
            order.paymentStatus == PaymentStatus.Paid && (
              <div className="payment-paid-banner">
                <FontAwesomeIcon icon={faCheckCircle} />
                Payment Paid
              </div>
            )}
          {order.paymentMethod == PaymentMethod.Card &&
            order.paymentStatus != PaymentStatus.Paid &&
            cookies.pyId &&
            cookies.pyOrderId == order.orderId && (
              <div className="alert alert-warning text-center">
                Payment in progress...
              </div>
            )}
          <div className="d-flex justify-content-between">
            <small className="">Placed at {formatDate(order.orderDate)}</small>
            <strong className={st.cssClass}>{st.text}</strong>
          </div>
          <div className="d-flex justify-content-between">
            <span>
              Order Type: <strong className={del.cssClass}>{del.text}</strong>
            </span>
          </div>
          {order.orderDeliveryType == DeliveryMethod.DineIn && (
            <div className="d-flex justify-content-between">
              <span>
                Table #: <b>{order.tableNumber}</b>
              </span>
            </div>
          )}

          <h6 className="mt-2">Order Summary</h6>
          <table className="table">
            <thead>
              <tr>
                <th>Items</th>
                <th className="text-end">Unit Price</th>
                <th className="text-end">Add-On Price</th>
                <th className="text-end">Subtotal</th>
              </tr>
            </thead>
            <tbody>
              {order.orderItems.map((item) => (
                <tr key={item.productId}>
                  {/* {!printView && (
                    <td className="col-2">
                      <img
                        src={
                          item.product?.productImagePath
                            ? `${AppConfig.APIBaseUrl}${item.product?.productImagePath}`
                            : defaultImgNa
                        }
                        alt="Product"
                        className="object-cover rounded-md"
                        height={55}
                        width={80}
                      />
                    </td>
                  )} */}

                  <td className="text-dark ms-2">
                    <span>
                      {item.product?.productName} x{" "}
                      <span className="badge bg-secondary">
                        {item.quantity}
                      </span>
                    </span>
                    <div>
                      {item.choosenAddOns?.split(",").map((ad) => (
                        <span className="badge bg-primary me-1 p-1 rounded-0 ps-1 pe-1">
                          {ad}
                        </span>
                      ))}
                    </div>
                    {item.offerType && (
                      <div>
                        {item.offerType == OfferTypeEnum.Buy1Get1 && (
                          <div className="text-success">Buy 1 Get 1 free</div>
                        )}
                        {item.offerType == OfferTypeEnum.FreeProduct && (
                          <div className="text-success">
                            Free item{" "}
                            <i className="fw-bold">{item.freeProductName}</i>
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td className="product-subtotal text-end">
                    <span className="subtotal-amount">
                      {item.discountForQuantity <= item.quantity &&
                      item.discountPrice ? (
                        <>
                          <del className="small">
                            {currencyFormatter(item.price)}
                          </del>
                          <div className="fs-6">
                            {currencyFormatter(item.discountPrice)}
                          </div>
                        </>
                      ) : (
                        currencyFormatter(item.price)
                      )}
                    </span>
                  </td>
                  <td className="product-subtotal text-end">
                    <span className="subtotal-amount">
                      {currencyFormatter(item.addOnsPrice)}
                    </span>
                  </td>
                  <td className="product-subtotal text-end">
                    <span className="subtotal-amount">
                      {currencyFormatter(item.totalAmount)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="mt-2">
            <div className="col offset-lg-6 offset-sm-0 text-end p-0">
              <table className="table table-borderless">
                <tbody>
                  <tr>
                    <td className="pt-0 pb-0">Sub-total:</td>
                    <td className="pt-0 pb-0">
                      {currencyFormatter(cartSubTotal)}
                    </td>
                  </tr>
                  <tr>
                    <td className="pt-0 pb-0">Tax:</td>
                    <td className="pt-0 pb-0">
                      {currencyFormatter(order.taxAmount ?? 0)}
                    </td>
                  </tr>
                  {order.voucherDiscount > 0 && (
                    <tr>
                      <td className="pt-0 pb-0 text-success">Discount:</td>
                      <td className="pt-0 pb-0 text-success">
                        -{currencyFormatter(order.voucherDiscount)}
                      </td>
                    </tr>
                  )}
                  {order.orderDeliveryType === DeliveryMethod.Delivery && (
                    <tr>
                      <td className="pt-0 pb-0">Delivery:</td>
                      <td className="pt-0 pb-0">
                        {currencyFormatter(order.deliveryCost ?? 0)}
                      </td>
                    </tr>
                  )}

                  <tr>
                    <td className="pt-0 pb-0 fw-bold">Order Total:</td>
                    <td className="pt-0 pb-0">
                      <b>{currencyFormatter(order.orderTotal)}</b>
                    </td>
                  </tr>
                  {order.amountPaid > 0 && (
                    <tr>
                      <td className="pt-0 pb-0 text-success">Amount paid:</td>
                      <td className="pt-0 pb-0 text-success">
                        {currencyFormatter(order.amountPaid)}
                      </td>
                    </tr>
                  )}
                  {order.paymentStatus === PaymentStatus.Pending &&
                    order.amountPaid > 0 && (
                      <tr>
                        <td className="pt-0 pb-0 fw-bold">Balance due:</td>
                        <td className="pt-0 pb-0 fw-bold">
                          {currencyFormatter(
                            Math.max(0, order.orderTotal - order.amountPaid)
                          )}
                        </td>
                      </tr>
                    )}
                </tbody>
              </table>
            </div>
          </div>
          <div className="mt-1">
            <h6>
              Payment:{" "}
              <small className="text-default">
                <strong className={pay.cssClass}>{pay.text}</strong>
              </small>
            </h6>
            {order.paymentStatus == PaymentStatus.Paid && (
              <div>
                <small>
                  Paid by{" "}
                  {order.paymentMethod == PaymentMethod.Cash ? "Cash" : "Card"}
                </small>
              </div>
            )}
          </div>
          {order.orderDeliveryType == DeliveryMethod.Collection && (
            <div className="mt-4">
              <h6>
                Collection Details:{" "}
                {order.orderStatus == OrderStatus.Completed ? (
                  <small className="text-secondary ms-2">
                    Order collected at{" "}
                    <strong className="">
                      {formatDate(order.dateUpdated)}
                    </strong>
                  </small>
                ) : (
                  <small className="text-default">
                    <strong>Collection Pending</strong>
                  </small>
                )}
              </h6>
              <div>
                <small>
                  Wanted Collection Date & Time:{" "}
                  <strong>
                    {formatDate(order.requestedCollectionDateTime)}
                  </strong>
                </small>
              </div>
              <div>
                <small>
                  Customer Phone:{" "}
                  <strong>
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="text-primary me-2"
                    />
                    {userDetails?.phoneNumber ?? "NA"}
                  </strong>
                </small>
              </div>
            </div>
          )}
          {order.orderDeliveryType == DeliveryMethod.Delivery && (
            <div className="mt-2">
              <h6>
                Delivery Details:{" "}
                {order.orderStatus == OrderStatus.Completed ? (
                  <small className="text-secondary ms-2">
                    Order delivered at{" "}
                    <strong>{formatDate(order.orderDeliveredDateTime)}</strong>
                  </small>
                ) : (
                  <small className="text-default">
                    <strong>Delivery Pending</strong>
                  </small>
                )}
              </h6>
              <div>
                <small>
                  Wanted Delivery Date & Time:{" "}
                  <strong>{formatDate(order.requestedDeliveryDateTime)}</strong>
                </small>
              </div>
              {order.orderDeliveryType == DeliveryMethod.Delivery &&
                (order.orderStatus == OrderStatus.OutForDelivery ||
                  isShopAdmin ||
                  isShopUser) && (
                  <h6>
                    Delivery Driver Details:{" "}
                    {order.deliveryDriver ? (
                      <>
                        <i className={vehType?.cssClass}></i>
                        <span className="ms-2">
                          {order.deliveryDriver?.name} (
                          {`${order.deliveryDriver?.contactNumber}${
                            order.deliveryDriver?.altContactNumber
                              ? `, ${order.deliveryDriver?.altContactNumber}`
                              : ""
                          }`}
                          )
                        </span>
                      </>
                    ) : (
                      <small className="text-secondary">NA</small>
                    )}
                  </h6>
                )}

              <h6 className="mb-1 mt-2">Delivery Address</h6>
              {order.deliveryAddress ? (
                <div>
                  <div>{order.deliveryAddress?.fullName}</div>
                  <div>{order.deliveryAddress?.addressLine1}</div>
                  <div>{order.deliveryAddress?.addressLine2}</div>
                  <div>{order.deliveryAddress?.city}</div>
                  <div>{order.deliveryAddress?.postcode}</div>
                  <div>
                    <FontAwesomeIcon
                      icon={faPhone}
                      className="text-primary me-2"
                    />{" "}
                    {order.deliveryAddress?.phoneNumber ??
                      userDetails?.phoneNumber ??
                      "NA"}
                  </div>
                </div>
              ) : (
                "Not available"
              )}
            </div>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <button className="btn btn-secondary" onClick={handlePrint}>
          <FontAwesomeIcon icon={faPrint} className="fs-4" />
          <span className="ms-1">Print</span>
        </button>
      </Modal.Footer>
      {/* Keep the compact receipt laid out off-screen so manual printing uses
          the same paper-saving format as automatic order printing. */}
      <div style={{ position: "fixed", top: 0, left: "-9999px" }}>
        <div ref={receiptRef}>
          <OrderReceipt order={order} />
        </div>
      </div>
    </Modal>
  );
});

export default OrderDetails;
